import { Navigate } from 'react-router-dom';
import { RootRouteError } from '@repo/error';
import type { ComponentType } from 'react';

import { reservationRoutes } from '@features/reservation';
import { MapLayout, parkingSearchRoutes } from '@features/parkingSearch';
import { ownedParkingCreateRoutes } from '@features/ownedParkingCreate';
import { ownedParkingManageRoutes } from '@features/ownedParkingManage';
import { accountRoutes } from '@features/account';
import {
  authRoutes,
  HANDOFF_ROUTE_PATH,
  HandoffPage,
  IMPERSONATION_ROUTE_PATH,
  ImpersonationPage,
} from '@features/auth';
import { reservationConflictRoutes } from '@features/reservationConflict';
import { filesRoutes } from '@features/files';

import Layout from '@shared/layouts/Layout';
import NotFoundPage from '@shared/pages/NotFoundPage';
import DefaultLayout from '@shared/layouts/DefaultLayout';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';
import type { AppRouteObject } from '@router/types';

interface CreateRoutesOptions {
  devRoutes?: AppRouteObject[];
  HomePage?: ComponentType | null;
}

export const createRoutes = ({ devRoutes = [], HomePage = null }: CreateRoutesOptions = {}): AppRouteObject[] => [
  {
    path: ROUTE_PATHS[RouteName.ROOT],
    name: RouteName.ROOT,
    element: <Layout />,
    errorElement: <RootRouteError />,
    children: [
      {
        index: true,
        name: RouteName.HOME,
        element: HomePage ? <HomePage /> : <Navigate to={ROUTE_PATHS[RouteName.MAP]} replace />,
      },
      {
        element: <MapLayout />,
        children: [...parkingSearchRoutes],
      },
      {
        // Children with soft orange header and body.
        element: <DefaultLayout headerTheme="soft-orange" theme="soft-orange" />,
        children: [...reservationRoutes, ...reservationConflictRoutes, ...filesRoutes],
      },
      {
        // Children with white header and soft-orange body.
        element: <DefaultLayout headerTheme="white" theme="soft-orange" />,
        children: [...ownedParkingCreateRoutes, ...ownedParkingManageRoutes, ...authRoutes],
      },
      {
        // Children with non standard header and body - element defined inside the feature.
        children: [...accountRoutes],
      },
      ...devRoutes,
      {
        path: IMPERSONATION_ROUTE_PATH,
        element: <ImpersonationPage />,
      },
      {
        path: HANDOFF_ROUTE_PATH,
        element: <HandoffPage />,
      },
      {
        path: '/sign-in',
        element: <Navigate to={ROUTE_PATHS[RouteName.MAP]} replace />,
      },
      {
        path: '/sign-up',
        element: <Navigate to={ROUTE_PATHS[RouteName.START_EARNING]} replace />,
      },
      {
        path: ROUTE_PATHS[RouteName.NOT_FOUND],
        name: RouteName.NOT_FOUND,
        element: <NotFoundPage />,
      },
    ],
  },
];
